<template>
  <v-container fluid pa-0>
    <!-- Initial Sizing of Individual Position -->
    <v-row>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-2">
          Initial Sizing of Individual Position
        </helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          All position sizing is defined in terms of <strong>Risk Units (RU)</strong>. A risk unit
          is a flexible abstraction — you define what one RU means below (e.g., 7% of portfolio,
          or a share count derived from ATR). The number you enter here is how many risk units are
          committed to each new position. Most strategies use <strong>1 RU</strong> per new buy.
          Setting 2 RU doubles the initial stake.
        </p>
        <v-row justify="start">
          <v-col cols="auto">
            <v-text-field
              v-model="criteria.risk_units"
              type="number"
              variant="outlined"
              density="compact"
              label="Risk Units per new position"
              hint="How many risk units to deploy per new buy signal (definition below)"
              persistent-hint
              :error-messages="parseFloat(criteria.risk_units) > 0 ? [] : ['Must be greater than 0']"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Define Risk Unit -->
    <helpers-section-sub-header>Define Risk Unit</helpers-section-sub-header>
    <p class="text-body-2 mb-2">
      Choose how one risk unit is calculated. The four options range from simple (fixed portfolio
      percentage) to volatility-adaptive (ATR or NATR-based share sizing). Volatility-based methods
      automatically reduce position size for riskier stocks and increase it for calmer ones,
      helping equalize the dollar impact each trade has on your portfolio.
    </p>
    <v-row>
      <v-col cols="12" md="6">
        <v-select
          v-model="criteria.risk_unit_type_id"
          :items="risk_unit_types"
          hide-details
          item-title="text"
          variant="outlined"
          density="compact"
          label="Risk Unit Definition"
        />
      </v-col>
    </v-row>

    <!-- Risk unit: % of Portfolio -->
    <v-row v-if="criteria.risk_unit_type_id == 1">
      <v-col cols="12">
        <p class="text-body-2 mt-2 mb-1">
          The simplest method: each buy is a fixed percentage of total portfolio value at the
          time of purchase. E.g., with a 7% setting on a $100,000 portfolio, each new position
          starts at $7,000 regardless of the stock's volatility.
        </p>
      </v-col>
      <v-col cols="auto">
        <v-text-field
          v-model="criteria.percent_of_portfolio"
          label="% of Portfolio per position"
          variant="outlined"
          density="compact"
          hint="Dollar amount invested = this % × current portfolio value"
          persistent-hint
          :error-messages="parseFloat(criteria.percent_of_portfolio) > 0 ? [] : ['Must be greater than 0']"
        />
      </v-col>
    </v-row>

    <!-- Risk unit: Volatility Adjusted Trade Sizing When Compared to S&P 500 -->
    <v-row v-if="criteria.risk_unit_type_id == 2">
      <v-col cols="12" xl="6">
        <p class="text-body-2 mt-2 mb-2">
          Scales position size based on a stock's volatility <em>relative to the S&amp;P 500</em>.
          Stocks that are calmer than the market get a larger allocation; stocks that are more
          volatile get a smaller one — keeping each trade's real-world risk roughly equal.
        </p>
        <p class="text-body-2 mb-1">How it works:</p>
        <ol class="text-body-2 mb-2">
          <li>
            Calculate the S&amp;P 500 (SPY) standard deviation for the selected time period,
            divided by SPY's closing price
          </li>
          <li>Repeat for the stock being purchased</li>
          <li>
            Divide the SPY result by the stock's result — this is the volatility multiplier
            (values below 1 mean the stock is more volatile than the S&amp;P)
          </li>
          <li>
            Apply: e.g., if your base position is 2% and the multiplier is 0.5 (stock is 2× as
            volatile as S&amp;P), the buy is sized at 2% × 0.5 = <strong>1%</strong>
          </li>
        </ol>
        <p class="text-body-2 mb-2">
          Use <strong>Min / Max Ratio</strong> to clamp the multiplier and prevent extreme
          position sizes in either direction.
        </p>
      </v-col>

      <v-container fluid>
        <v-row>
          <v-col cols="12" sm="4" md="3" xl="2">
            <v-select
              v-model="criteria.volatility_time_period_id"
              :items="volatility_time_periods"
              item-title="text"
              variant="outlined"
              density="compact"
              label="Time Period"
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="4" md="3" xl="2">
            <v-text-field
              v-model="criteria.percent_of_portfolio"
              label="% of Portfolio"
              variant="outlined"
              density="compact"
              hint="entry % of portfolio"
              persistent-hint
              :error-messages="parseFloat(criteria.percent_of_portfolio) > 0 ? [] : ['Must be greater than 0']"
            />
          </v-col>
          <v-col cols="12" sm="12" md="8" xl="5">
            <v-select
              v-model="criteria.volatility_sizing"
              :items="volatility_sizing"
              item-title="text"
              variant="outlined"
              density="compact"
              label="Sizing Direction"
              hide-details
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" sm="4" md="3" xl="2">
            <v-text-field
              v-model="criteria.volatility_min_ratio"
              type="number"
              variant="outlined"
              density="compact"
              label="Min ratio"
              hide-details
            />
          </v-col>

          <v-col cols="12" sm="4" md="3" xl="2">
            <v-text-field
              v-model="criteria.volatility_max_ratio"
              type="number"
              variant="outlined"
              density="compact"
              label="Max Ratio"
              hide-details
            />
          </v-col>
        </v-row>
      </v-container>
    </v-row>

    <!-- Risk unit: % of Portfolio Divided by (Average True Range ATR x Share Price) -->
    <v-row v-if="criteria.risk_unit_type_id == 3">
      <v-col cols="12">
        <v-row>
          <v-col cols="12" md="8" xl="6">
            <p class="text-body-2 mt-2 mb-2">
              Sizes positions in shares so that each trade's dollar risk (if stopped out at 1 ATR)
              is equal to a fixed percentage of portfolio. Volatile stocks get fewer shares;
              calm stocks get more — each position has the same theoretical risk budget.
            </p>
            <p class="text-body-2 mb-1">
              <strong>True Range</strong> for a day is the greatest of:
            </p>
            <ul class="text-body-2 mb-2">
              <li>Day's high minus day's low</li>
              <li>Day's high minus previous close (absolute value)</li>
              <li>Day's low minus previous close (absolute value)</li>
            </ul>
            <p class="text-body-2 mb-2">
              ATR is a moving average of True Range over the number of days you specify.
            </p>
            <p class="text-body-2 mb-1"><strong>Example:</strong></p>
            <p class="text-body-2 mb-1">
              Portfolio: $100,000 &nbsp;|&nbsp; Base %: 2% &nbsp;|&nbsp; 14-day ATR: $5.65
              &nbsp;|&nbsp; Price: $181.17
            </p>
            <p class="text-body-2 mb-0">
              Shares = (2% × $100,000) ÷ (5.65 × 181.17) = <strong>1.95 → 2 shares</strong>
              &nbsp;(rounded up)
            </p>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="auto">
        <v-text-field
          v-model="criteria.percent_of_portfolio"
          label="% of Portfolio"
          variant="outlined"
          density="compact"
          hint="entry % of portfolio"
          persistent-hint
        />
      </v-col>
      <v-col cols="12" d-flex align="end">
        <v-row>
          <v-col :shrink="!$vuetify.display.xs" cols="12">
            <v-select
              v-model="criteria.atr_time_period_id"
              :items="atr_time_periods"
              item-title="text"
              hide-details
              variant="outlined"
              density="compact"
              style="max-width: 250px"
              label="ATR Time Period # of Days"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Risk unit: % of Portfolio Divided by (Average True Range ATR x Share Price) -->
    <v-row v-if="criteria.risk_unit_type_id == 4">
      <v-col cols="12">
        <v-row>
          <v-col cols="12" md="8" xl="6">
            <p class="text-body-2 mt-2 mb-2">
              Similar to ATR-based sizing, but uses <strong>Normalized ATR (NATR)</strong> — ATR
              expressed as a percentage of the stock's price. This makes volatility comparable
              across stocks at very different price levels (e.g., a $10 stock vs. a $500 stock).
              Lower-NATR stocks (calmer relative to their price) receive a larger initial stake;
              higher-NATR stocks get a smaller one.
            </p>
            <p class="text-body-2 mb-1"><strong>Example:</strong></p>
            <p class="text-body-2 mb-1">
              Portfolio: $100,000 &nbsp;|&nbsp; Base %: 2% &nbsp;|&nbsp; 14-day ATR: $5.65
              &nbsp;|&nbsp; Price: $181.17
            </p>
            <p class="text-body-2 mb-1">
              NATR = (5.65 ÷ 181.17) × 100 = <strong>3.1</strong>
            </p>
            <p class="text-body-2 mb-0">
              Shares = (2% × $100,000) ÷ (3.1 × 181.17) = <strong>3.56 → 4 shares</strong>
              &nbsp;(rounded up)
            </p>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="auto">
        <v-text-field
          v-model="criteria.percent_of_portfolio"
          label="% of Portfolio"
          variant="outlined"
          density="compact"
          hint="entry % of portfolio"
          persistent-hint
        />
      </v-col>
      <v-col cols="12" d-flex align="end">
        <v-row>
          <v-col :shrink="!$vuetify.display.xs" cols="12">
            <v-select
              v-model="criteria.atr_time_period_id"
              :items="atr_time_periods"
               item-title="text"
              hide-details
              variant="outlined"
              density="compact"
              style="max-width: 250px"
              label="ATR Time Period # of Days"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-2">
          Further risk adjust trades based on S&amp;P price vs SMA:
        </helpers-section-sub-header>
        <p class="text-body-2 mb-1">
          An optional second-order adjustment that makes the backtester more conservative when
          the market is extended above its moving average, and more aggressive after pullbacks.
          For each new trade, the final share count is multiplied by
          <code>(SMA of S&amp;P ÷ current S&amp;P price)</code>. When the S&amp;P is above its
          SMA the multiplier is below 1.0, reducing position size. When the S&amp;P is below its
          SMA (after a decline) the multiplier exceeds 1.0, increasing position size.
        </p>
        <p class="text-body-2 mb-2">
          Squaring the multiplier amplifies this effect — useful if you want aggressive
          mean-reversion scaling.
        </p>
        <v-checkbox
          v-model="criteria.sma_adjusted_enabled"
          label="Enable S&amp;P vs SMA adjusting"
          hide-details
        />
        <v-row no-gutters justify="start">
          <v-col>
            <v-row justify="start">
              <v-col cols="auto">
                <v-text-field
                  v-model="criteria.sma_adjusted_days"
                  type="number"
                  variant="outlined"
                  density="compact"
                  label="SMA days"
                  hint="# of Calendar days for moving average"
                  persistent-hint
                  :error-messages="criteria.sma_adjusted_enabled && !(parseInt(criteria.sma_adjusted_days) > 0) ? ['Required when SMA adjustment is enabled'] : []"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="criteria.sma_adjusted_square"
                  label="Square multiplier for greater effect"
                  color="primary"
                  hide-details
                  class="mt-0"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-2">
          Limit position size to a Percentage of Average Daily Dollar Volume (ADDV):
        </helpers-section-sub-header>
        <p class="text-body-2 mb-1">
          Caps the dollar value of any single buy order to a percentage of the stock's average
          daily trading volume. Calculated using the past 20 trading days of price × volume.
        </p>
        <p class="text-body-2 mb-2">
          This is critical for realistic backtesting with <strong>small-cap and micro-cap
          stocks</strong>. A position that is 50% of a stock's daily volume would be very
          difficult (and market-moving) to fill in practice. A setting of <strong>5–10%</strong>
          keeps simulated entries within realistic liquidity limits.
        </p>

        <v-checkbox v-model="criteria.addv_adjusted_enabled" label="Enable ADDV Max" hide-details />
        <v-row no-gutters justify="start">
          <v-col>
            <v-row justify="start">
              <v-col cols="auto">
                <v-text-field
                  v-model="criteria.addv_adjusted_percent"
                  type="number"
                  variant="outlined"
                  density="compact"
                  label="Max ADDV Percent"
                  hint="percentage from 0 to 100"
                  persistent-hint
                  :error-messages="criteria.addv_adjusted_enabled && !(parseFloat(criteria.addv_adjusted_percent) > 0) ? ['Required when ADDV max is enabled'] : []"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- How to treat buy signals for existing positions -->
    <v-row>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-2">
          How to treat buy signals for existing positions:
        </helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          Controls what happens when a buy signal fires for a stock you already hold.
          <strong>Do NOT add</strong> keeps position size fixed — useful for strategies where
          you want strict position limits. The <strong>add-to</strong> options let you
          pyramid into winning positions, which can amplify gains but also increases concentration
          risk. The add amount can be defined as a fixed % of portfolio, a % of your last or
          initial buy, or additional risk units.
        </p>
        <v-row no-gutters justify="start">
          <v-col>
            <v-radio-group v-model="criteria.existing_position_buy_action_id" hide-details>
              <v-radio
                :value="1"
                label="Do NOT add to existing positions when buy signal matches"
              />
              <v-radio :value="2" label="Add at % of Portfolio" />
              <v-row v-show="criteria.existing_position_buy_action_id === 2">
                <v-col cols="auto">
                  <v-text-field
                    v-model="criteria.existing_add_unit"
                    label="% of Portfolio"
                    variant="outlined"
                    type="number"
                    min="0"
                    :error-messages="parseFloat(criteria.existing_add_unit) > 0 ? [] : ['Must be greater than 0']"
                  />
                </v-col>
              </v-row>
              <v-radio :value="3" label="Add at % of last buy value" />
              <div>
                <v-row v-show="criteria.existing_position_buy_action_id === 3">
                  <v-col cols="auto">
                    <v-text-field
                      v-model="criteria.existing_add_unit"
                      label="% of last buy value"
                      type="number"
                      variant="outlined"
                      min="0"
                      :error-messages="parseFloat(criteria.existing_add_unit) > 0 ? [] : ['Must be greater than 0']"
                    />
                  </v-col>
                </v-row>
              </div>
              <v-radio :value="4" label="Add at % of initial buy value" />
              <v-row v-show="criteria.existing_position_buy_action_id === 4">
                <v-col cols="auto">
                  <v-text-field
                    v-model="criteria.existing_add_unit"
                    variant="outlined"
                    label="% of initial buy value"
                    type="number"
                    min="0"
                    :error-messages="parseFloat(criteria.existing_add_unit) > 0 ? [] : ['Must be greater than 0']"
                  />
                </v-col>
              </v-row>
              <v-radio :value="5" label="Add # of Risk Units" />
              <v-row v-show="criteria.existing_position_buy_action_id === 5">
                <v-col cols="auto">
                  <v-text-field
                    v-model="criteria.existing_add_unit"
                    variant="outlined"
                    label="Number of risk units"
                    type="number"
                    min="0"
                    :error-messages="parseFloat(criteria.existing_add_unit) > 0 ? [] : ['Must be greater than 0']"
                  />
                </v-col>
              </v-row>
            </v-radio-group>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Slippage -->
    <v-row>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-2">
          Stock Price to Use for Buys:
        </helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          Buy signals are generated after the market closes. <strong>Closing price</strong>
          simulates buying at that same close (optimistic, but consistent). <strong>Opening
          price</strong> simulates placing an order the following morning — more realistic for
          most traders, though it may miss large gaps up. Pair this with the slippage setting
          on the General tab for a more conservative simulation.
        </p>
        <v-row no-gutters>
          <v-col cols="auto">
            <v-select
              v-model="criteria.stock_price_to_use"
              width="200"
              variant="outlined"
              :items="['closing', 'opening']"
              hide-details
              label="Execution price for buy orders"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- How to manage new buy signals when fully-invested -->
    <v-row>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-2">
          How to manage new buy signals when fully-invested:
        </helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          When the portfolio has no free cash to fund a new buy signal, these rules determine
          which existing holding (if any) should be sold to make room. Rules are evaluated in
          priority order (lowest number first). Add multiple rules to create a decision
          waterfall — the backtester tries each rule in order until one matches.
        </p>
        <p class="text-body-2 mb-2">
          Use <strong>"Prevent selling existing holding if also a new signal"</strong> to avoid
          selling a stock the same day a fresh buy signal fires for it — which would immediately
          re-buy the same position and waste the transaction.
        </p>

        <v-col cols="12">
          <v-row no-gutters>
            <v-checkbox
              v-model="criteria.prevent_holding_with_signal_from_being_sold"
              hide-details
              density="compact"
              class="shrink mr-2"
              label="Prevent selling existing holding if also a new signal"
            />
          </v-row>
        </v-col>
        <v-row>
          <v-col cols="12" sm="10" md="8" lg="6">
            <v-select
              v-model="new_fully_invested_rule"
              :items="fully_invested_actions"
              return-object
              variant="outlined"
              density="compact"
              item-title="text"
              label="Rule Type"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" dark class="mb-2" @click="add_fully_invested_rule()">
              Add New Rule
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-data-table
              density="compact"
              disable-sort
              items-per-page="-1"
              :headers="fully_invested_headers"
              :items="criteria.fully_invested_actions"
              hide-default-footer
              class="elevation-1"
            >
              <template #item.enabled="{ item }">
                <v-checkbox v-model="item.enabled" />
              </template>
              <template #item.ordering="{ item }">
                {{ item.ordering }}
              </template>
              <template #item.actions="{ item }">
                <v-icon size="small" @click="edit(item)">
                  {{ mdiPencil }}
                </v-icon>
                <v-icon size="small" @click="delete_item(item)">
                  {{ mdiDelete }}
                </v-icon>
              </template>
              <template #item.description="{ item }">
                <div
                  v-if="item.value == 1 || item.value == 2 || item.value == 3 || item.value == 4"
                >
                  {{ item.text }}
                </div>
                <div v-else-if="item.value == 5">
                  Sell out-performing issues provided they are up at least {{ item.percentage }}%
                  <span v-if="item.vs_sp_enabled"
                    >Stock has outperformed S&amp;P by {{ item.vs_sp_threshold }}%. Stock has
                    outperformed by at least {{ item.vs_sp_significance }} percentage points
                  </span>
                </div>
                <div v-else-if="item.value == 6">
                  Sell underperforming issues provided they are down at least {{ item.percentage }}%
                  <span v-if="item.vs_sp_enabled"
                    >Stock has underperformed S&amp;P by {{ item.vs_sp_threshold }}%. Has
                    underperformed by at least {{ item.vs_sp_significance }} percentage points
                  </span>
                </div>
                <div v-else-if="item.value == 7">
                  Sell securities at an {{ get_time_period(item.exit_time_period_id) }} low
                  <span v-if="item.has_outperformed">security has outperformed</span>
                  <span v-else-if="item.has_underperformed">security has underperformed</span>
                </div>
                <div v-else-if="item.value == 8">
                  Sell securities at an {{ get_time_period(item.exit_time_period_id) }} high
                  <span v-if="item.has_outperformed">security has outperformed</span>
                  <span v-else-if="item.has_underperformed">security has underperformed</span>
                </div>
                <div v-else-if="item.value == 9">
                  Sell securities with past {{ item.trailing_stop_days }} days performance &lt;
                  {{ item.percentage }}%
                  <span v-if="item.minimum_held_days && item.minimum_held_days != ''"
                    >and held at least {{ item.minimum_held_days }} days.</span
                  >
                  <span v-if="item.vs_sp_enabled"
                    >Stock has outperformed S&amp;P by {{ item.vs_sp_threshold }}%. Has out
                    performed by at least {{ item.vs_sp_significance }} percentage points.
                  </span>
                </div>
                <div v-else-if="item.value == 10">
                  Sell securities with trailing ATR price decline&gt;= {{ item.percentage }} over
                  the past {{ item.trailing_stop_days }} days
                  <span v-if="item.minimum_held_days && item.minimum_held_days != ''"
                    >held at least {{ item.minimum_held_days }} days.</span
                  >
                  <span v-if="item.stock_up_enabled"
                    >stock must be up at least {{ item.stock_up_at_least }}%</span
                  >
                  <span v-if="item.vs_sp_enabled"
                    >Stock has outperformed S&amp;P by {{ item.vs_sp_threshold }}%. perf is greater
                    than {{ item.vs_sp_significance }}%
                  </span>
                </div>
                <div v-else-if="item.value == 11">
                  Sell securities with trailing ATR price decline&gt;= {{ item.percentage }} over
                  the past {{ item.trailing_stop_days }} days,
                  <span v-if="item.minimum_held_days && item.minimum_held_days != ''"
                    >held at least {{ item.minimum_held_days }} days.</span
                  >
                  <span v-if="item.stock_down_enabled"
                    >stock must be down at least {{ item.stock_down_at_least }}%</span
                  >
                  <span v-if="item.vs_sp_enabled"
                    >Stock has underperformed S&amp;P by {{ item.vs_sp_threshold }}%. Stock has
                    underperformed by at least {{ item.vs_sp_significance }} percentage points
                  </span>
                </div>
                <div v-else-if="item.value == 12">
                  Sell securities when current {{ get_time_period(item.exit_time_period_id) }} high
                  is greater than {{ get_time_period(item.exit_time_period_id) }} high at purchase
                  plus {{ item.atr }} ATRs
                </div>
                <div v-else-if="item.value == 13">
                  Sell securities with ADDV
                  <span v-if="item.addv_inverse">ascending (least liquid first)</span>
                  <span v-else>greater than {{ item.percentage }}</span>
                </div>
                <div v-else-if="item.value == 14">
                  Sell position with fewest originating institutional signals
                </div>
                <div v-else-if="item.value == 15">
                  Partial trim: sell {{ item.percentage }}% of each holding until enough cash freed
                </div>
                <div v-else-if="item.value == 17">
                  Force-rotate holdings older than {{ item.max_age_days }} days
                </div>
                <div v-else-if="item.value == 18">
                  Sector cap rotation: sell worst performer in most over-weight sector
                </div>
                <div v-else-if="item.value == 19">
                  Sell holdings down {{ item.percentage }}% or more from their peak price
                </div>
                <div v-else-if="item.value == 20">
                  Volatility budget: sell holding with highest (% of portfolio × ATR/price)
                </div>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-dialog v-model="fully_invested_dialog" persistent max-width="650px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">Fully Invested Rule</span>
        </v-card-title>
        <v-card-text style="height: 400px">
          <v-container>
            <v-row density="compact">
              <v-col cols="12">
                <h4>{{ edit_rule.text }}</h4>
              </v-col>
              <v-col cols="12">
                <v-checkbox v-model="edit_rule.enabled" density="compact" label="Enabled" />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="edit_rule.ordering"
                  label="Ordering"
                  hint="low # chosen first"
                  style="max-width: 150px"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>

              <v-col v-if="edit_rule.value == 5" cols="12" sm="6" md="6">
                <v-text-field
                  v-if="edit_rule.value == 5"
                  v-model="edit_rule.percentage"
                  type="number"
                  suffix="%"
                  style="max-width: 150px"
                  label="% Up"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
              <v-col v-if="edit_rule.value == 6" cols="12" sm="6" md="6">
                <v-text-field
                  v-if="edit_rule.value == 6"
                  v-model="edit_rule.percentage"
                  type="number"
                  suffix="%"
                  label="% Down"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>

              <v-col v-if="edit_rule.value == 7" cols="12" sm="6" md="6">
                <v-select
                  v-if="edit_rule.value == 7"
                  v-model="edit_rule.exit_time_period_id"
                  :items="exit_time_periods"
                  label="time period"
                  class="shrink"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
              <v-col v-if="edit_rule.value == 7" cols="12" sm="12" md="12">
                <v-checkbox
                  v-model="edit_rule.has_outperformed"
                  density="compact"
                  label="Enable Only if security has outperformed since purchased"
                  @click="edit_rule.has_underperformed = false"
                />
                <v-checkbox
                  v-model="edit_rule.has_underperformed"
                  density="compact"
                  label="Enable Only if security has underperformed since purchased"
                  @click="edit_rule.has_outperformed = false"
                />
              </v-col>
              <v-col v-if="edit_rule.value == 8 || edit_rule.value == 12" cols="12" sm="6" md="6">
                <v-select
                  v-if="edit_rule.value == 8 || edit_rule.value == 12"
                  v-model="edit_rule.exit_time_period_id"
                  label="time period"
                  :items="exit_time_periods"
                  class="shrink"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
              <v-col v-if="edit_rule.value == 12" cols="12" sm="6" md="6">
                <v-text-field
                  v-if="edit_rule.value == 12"
                  v-model="edit_rule.atr"
                  type="number"
                  suffix=""
                  style="max-width: 150px"
                  label="# ATRs"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
              <v-col v-if="edit_rule.value == 8" cols="12" sm="12" md="12">
                <v-checkbox
                  v-model="edit_rule.has_outperformed"
                  density="compact"
                  label="Enable Only if security has outperformed since purchased"
                  @click="edit_rule.has_underperformed = false"
                />
                <v-checkbox
                  v-model="edit_rule.has_underperformed"
                  density="compact"
                  label="Enable Only if security has underperformed since purchased"
                  @click="edit_rule.has_outperformed = false"
                />
              </v-col>
              <v-col
                v-if="edit_rule.value == 9 || edit_rule.value == 10 || edit_rule.value == 11"
                cols="12"
                sm="6"
                md="6"
              >
                <v-text-field
                  v-if="edit_rule.value == 9 || edit_rule.value == 10 || edit_rule.value == 11"
                  v-model="edit_rule.trailing_stop_days"
                  type="number"
                  suffix=""
                  label="past # days perf"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
              <v-col v-if="edit_rule.value == 9" cols="12" sm="6" md="6">
                <v-text-field
                  v-if="edit_rule.value == 9"
                  v-model="edit_rule.percentage"
                  type="number"
                  suffix="%"
                  label="Perf Less Than"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
              <v-col v-if="edit_rule.value == 10 || edit_rule.value == 11" cols="12" sm="6" md="6">
                <v-text-field
                  v-if="edit_rule.value == 10 || edit_rule.value == 11"
                  v-model="edit_rule.percentage"
                  type="number"
                  label="ATR Decline"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
              <v-col
                v-if="edit_rule.value == 9 || edit_rule.value == 10 || edit_rule.value == 11"
                cols="12"
                sm="6"
                md="6"
              >
                <v-text-field
                  v-if="edit_rule.value == 9 || edit_rule.value == 10 || edit_rule.value == 11"
                  v-model="edit_rule.minimum_held_days"
                  type="number"
                  label="min. days held"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
            </v-row>
            <v-row v-if="edit_rule.value == 10" density="compact">
              <v-col cols="12">
                <v-checkbox
                  v-model="edit_rule.stock_up_enabled"
                  density="compact"
                  label="Enable Optional Sell Only When Stock is Up"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="edit_rule.stock_up_at_least"
                  :disabled="!edit_rule.stock_up_enabled"
                  type="number"
                  label="Stock up at least ___%"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
            </v-row>

            <v-row v-if="edit_rule.value == 13" density="compact">
              <v-col cols="6">
                <v-checkbox
                  v-model="edit_rule.addv_inverse"
                  density="compact"
                  label="Inverse mode: sell least liquid first (ignores threshold below)"
                  hide-details
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="edit_rule.percentage"
                  :disabled="!edit_rule.enabled || edit_rule.addv_inverse"
                  type="number"
                  label="ADDV greater than ___"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
            </v-row>

            <v-row v-if="edit_rule.value == 11" density="compact">
              <v-col cols="12">
                <v-checkbox
                  v-model="edit_rule.stock_down_enabled"
                  density="compact"
                  label="Enable Optional Sell Only When Stock is Down"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="edit_rule.stock_down_at_least"
                  :disabled="!edit_rule.stock_down_enabled"
                  type="number"
                  label="Optional:  Stock down at least ___%"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
            </v-row>

            <v-row
              v-if="edit_rule.value == 5 || edit_rule.value == 10 || edit_rule.value == 9"
              density="compact"
            >
              <v-col cols="12">
                <v-checkbox
                  v-model="edit_rule.vs_sp_enabled"
                  density="compact"
                  label="Enable Only Sell if Outperformed vs S&amp;P"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="edit_rule.vs_sp_threshold"
                  :disabled="!edit_rule.vs_sp_enabled"
                  type="number"
                  label="Percent"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="edit_rule.vs_sp_significance"
                  :disabled="!edit_rule.vs_sp_enabled"
                  type="number"
                  label=" at least Percentage Points"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
            </v-row>

            <v-row v-if="edit_rule.value == 11 || edit_rule.value == 6" density="compact">
              <v-col cols="12">
                <v-checkbox
                  v-model="edit_rule.vs_sp_enabled"
                  density="compact"
                  label="Enable Only Sell if Underperformed vs S&amp;P"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="edit_rule.vs_sp_significance"
                  :disabled="!edit_rule.vs_sp_enabled"
                  type="number"
                  label="at least __Percentage Points"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
            </v-row>
            <v-row v-if="edit_rule.value == 15" density="compact">
              <v-col cols="6">
                <v-text-field
                  v-model="edit_rule.percentage"
                  :disabled="!edit_rule.enabled"
                  type="number"
                  label="Trim % per holding"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
            </v-row>

            <v-row v-if="edit_rule.value == 17" density="compact">
              <v-col cols="6">
                <v-text-field
                  v-model="edit_rule.max_age_days"
                  :disabled="!edit_rule.enabled"
                  type="number"
                  label="Max holding age (days)"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
            </v-row>

            <v-row v-if="edit_rule.value == 19" density="compact">
              <v-col cols="6">
                <v-text-field
                  v-model="edit_rule.percentage"
                  :disabled="!edit_rule.enabled"
                  type="number"
                  label="Peak drawdown % threshold"
                  density="compact"
                  hide-details
                  variant="outlined"
                />
              </v-col>
            </v-row>

            <v-row />
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="blue-darken-1" text @click="close()"> Cancel </v-btn>
          <v-btn color="blue-darken-1" text @click="save()"> Save </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// Modules
import cloneDeep from 'lodash/cloneDeep'
import { mapWritableState } from 'pinia'
import { useUserStore, useAppStore } from '@/store'
import { mdiDelete, mdiPencil } from '@mdi/js'
export default {
  props: {
    ruleType: {
      type: String,
      required: true,
      validator: value => ['long', 'short'].includes(value)
    }
  },
  data() {
    return {
      mdiDelete,
      mdiPencil,
      fully_invested_dialog: false,
      new_fully_invested_rule: null,
      editing_index: -1,
      edit_rule: {},
      fully_invested_headers: [
        {
          title: 'Enabled',
          align: 'left',
          sortable: false,
          key: 'enabled'
        },
        {
          title: 'Description',
          align: 'left',
          sortable: false,
          key: 'description'
        },
        {
          title: 'Priority',
          align: 'left',
          sortable: true,
          key: 'ordering'
        },
        { title: 'Actions', key: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    ...mapWritableState(useAppStore, [
      'whaleTraderCriteria',
      'risk_unit_types',
      'atr_time_periods',
      'volatility_time_periods',
      'volatility_sizing',
      'exit_time_periods',
      'fully_invested_actions'
    ]),
    ...mapWritableState(useUserStore, [
      'tradingModels',
      'sharedModels',
      'trading_model_update',
      'status_update_snackbar',
      'user',
      'tradingModels'
    ]),

    // Computed criteria based on ruleType prop - avoids prop mutation warning
    criteria() {
      return this.whaleTraderCriteria.entry_rules[this.ruleType]
    }
  },
  methods: {
    get_time_period(id) {
      var found = ''
      for (var i = 0; i < this.exit_time_periods.length; i++) {
        if (this.exit_time_periods[i].value == id) {
          found = this.exit_time_periods[i].text
          break
        }
      }
      return found
    },
    add_fully_invested_rule() {
      if (!this.new_fully_invested_rule) {
        return
      }
      this.editing_index = -1
      this.edit_rule = cloneDeep(this.new_fully_invested_rule)
      this.edit_rule.enabled = true
      this.fully_invested_dialog = true
    },
    edit(item) {
      this.editing_index = this.criteria.fully_invested_actions.indexOf(item)
      this.edit_rule = Object.assign({}, item)
      this.fully_invested_dialog = true
    },
    save() {
      if (this.editing_index < 0) {
        this.criteria.fully_invested_actions.push(this.edit_rule)
      } else {
        Object.assign(this.criteria.fully_invested_actions[this.editing_index], this.edit_rule)
      }
      this.close()
    },
    close() {
      this.editing_index = -1
      this.edit_rule = {}
      this.fully_invested_dialog = false
    },
    delete_item(item) {
      var index = this.criteria.fully_invested_actions.indexOf(item)
      if (confirm('Are you sure you want to delete this rule?')) {
        this.criteria.fully_invested_actions.splice(index, 1)
      }
    }
  }
}
</script>
